import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { BACKEND_URL } from '../config';
import {
  Crosshair,
  CalendarCheck,
  Phone,
  FileText,
  ClipboardText,
  CaretRight,
  Lightning,
  CheckCircle,
  CircleNotch,
  ArrowRight,
  PlugsConnected,
  Play,
} from '@phosphor-icons/react';

const ICON_MAP = {
  target: Crosshair,
  calendar: CalendarCheck,
  phone: Phone,
  'file-text': FileText,
  clipboard: ClipboardText,
};

export default function OnboardingFlow({ onComplete }) {
  const [step, setStep] = useState('loading');
  const [outcomes, setOutcomes] = useState([]);
  const [selectedOutcome, setSelectedOutcome] = useState(null);
  const [deployResult, setDeployResult] = useState(null);
  const [testOutput, setTestOutput] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    checkStatus();
  }, []);

  const checkStatus = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/onboarding/status`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to check status');
      const data = await res.json();

      if (data.status === 'completed') {
        onComplete?.();
        return;
      }

      if (data.status === 'deployed') {
        setDeployResult({
          agent_id: data.agent_id,
          agent_name: data.agent_name,
          test_input: data.test_input,
          recommended_integrations: data.recommended_integrations || [],
        });
        setStep('deployed');
        return;
      }

      // Not started or in progress — show prompt
      await fetchPrompt();
    } catch {
      await fetchPrompt();
    }
  };

  const fetchPrompt = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/onboarding/prompt`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to load');
      const data = await res.json();
      setOutcomes(data.outcomes || []);

      if (data.completed) {
        onComplete?.();
        return;
      }
      setStep('prompt');
    } catch {
      setStep('prompt');
      setOutcomes([]);
    }
  };

  const handleSelect = async (outcomeId) => {
    setSelectedOutcome(outcomeId);
    setLoading(true);
    setError(null);

    try {
      const res = await fetch(`${BACKEND_URL}/api/onboarding/select`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ outcome_id: outcomeId }),
      });

      if (!res.ok) throw new Error('Failed to deploy system');
      const data = await res.json();
      setDeployResult(data);
      setStep('deployed');
    } catch (e) {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleTestRun = async () => {
    if (!deployResult?.agent_id) return;
    setLoading(true);
    setError(null);

    try {
      const res = await fetch(`${BACKEND_URL}/api/onboarding/test-run`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          agent_id: deployResult.agent_id,
          input_text: deployResult.test_input || '',
        }),
      });

      if (!res.ok) throw new Error('Test run failed');
      const data = await res.json();
      setTestOutput(data);
      setStep('result');
    } catch (e) {
      setError('Test run failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (step === 'loading') {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#0a0a0f]/90 backdrop-blur-sm">
        <CircleNotch size={32} className="text-white/40 animate-spin" />
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#0a0a0f]/95 backdrop-blur-md p-4" data-testid="onboarding-flow">
      <AnimatePresence mode="wait">
        {/* ── STEP 1: What do you want to automate? ── */}
        {step === 'prompt' && (
          <motion.div
            key="prompt"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -24 }}
            transition={{ duration: 0.3 }}
            className="w-full max-w-2xl"
          >
            <div className="text-center mb-8">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/5 border border-white/10 text-xs text-white/50 mb-4">
                <Lightning size={14} weight="fill" className="text-amber-400" />
                Get your first result in under 2 minutes
              </div>
              <h1 className="text-3xl sm:text-4xl font-bold text-white mb-2" data-testid="onboarding-title">
                What do you want to automate?
              </h1>
              <p className="text-white/40 text-sm">
                Select an outcome and we'll set everything up for you.
              </p>
            </div>

            <div className="space-y-3" data-testid="outcome-options">
              {outcomes.map((o, i) => {
                const Icon = ICON_MAP[o.icon] || Lightning;
                const isSelected = selectedOutcome === o.id;
                return (
                  <motion.button
                    key={o.id}
                    initial={{ opacity: 0, x: -12 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.06 }}
                    onClick={() => handleSelect(o.id)}
                    disabled={loading}
                    data-testid={`outcome-${o.id}`}
                    className={`w-full flex items-center gap-4 px-5 py-4 rounded-xl border text-left transition-all duration-200
                      ${isSelected && loading
                        ? 'border-white/30 bg-white/10'
                        : 'border-white/8 bg-white/[0.03] hover:border-white/20 hover:bg-white/[0.06]'
                      }
                      disabled:opacity-60 disabled:cursor-wait
                    `}
                  >
                    <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-white/5 flex items-center justify-center">
                      <Icon size={20} className="text-white/60" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-white">{o.label}</div>
                      <div className="text-xs text-white/35 mt-0.5 truncate">{o.description}</div>
                    </div>
                    {isSelected && loading ? (
                      <CircleNotch size={18} className="text-white/40 animate-spin flex-shrink-0" />
                    ) : (
                      <CaretRight size={16} className="text-white/20 flex-shrink-0" />
                    )}
                  </motion.button>
                );
              })}
            </div>

            {error && (
              <p className="text-red-400/80 text-xs text-center mt-4">{error}</p>
            )}

            <button
              onClick={() => onComplete?.()}
              className="block mx-auto mt-6 text-xs text-white/25 hover:text-white/40 transition-colors"
              data-testid="skip-onboarding"
            >
              Skip for now
            </button>
          </motion.div>
        )}

        {/* ── STEP 2: System deployed ── */}
        {step === 'deployed' && deployResult && (
          <motion.div
            key="deployed"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -24 }}
            transition={{ duration: 0.3 }}
            className="w-full max-w-lg"
          >
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-emerald-500/10 border border-emerald-500/20 mb-4">
                <CheckCircle size={24} weight="fill" className="text-emerald-400" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-1" data-testid="deployed-title">
                System ready
              </h2>
              <p className="text-white/40 text-sm">
                <span className="text-white/70 font-medium">{deployResult.agent_name}</span> is set up and ready to run.
              </p>
            </div>

            {/* Recommended integrations */}
            {deployResult.recommended_integrations?.length > 0 && (
              <div className="mb-5 p-4 rounded-xl bg-white/[0.03] border border-white/8">
                <div className="flex items-center gap-2 mb-2">
                  <PlugsConnected size={16} className="text-white/40" />
                  <span className="text-xs text-white/50 font-medium">Recommended connections</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {deployResult.recommended_integrations.map((int) => (
                    <span
                      key={int}
                      className="px-2.5 py-1 rounded-md bg-white/5 border border-white/8 text-xs text-white/50"
                    >
                      {int.replace(/_/g, ' ')}
                    </span>
                  ))}
                </div>
                <p className="text-[11px] text-white/25 mt-2">
                  Optional — connect later in Settings &gt; Integrations.
                </p>
              </div>
            )}

            {/* Test input preview */}
            {deployResult.test_input && (
              <div className="mb-5 p-4 rounded-xl bg-white/[0.03] border border-white/8">
                <div className="text-xs text-white/40 mb-1.5">Test input</div>
                <p className="text-xs text-white/60 leading-relaxed line-clamp-3">
                  {deployResult.test_input}
                </p>
              </div>
            )}

            <button
              onClick={handleTestRun}
              disabled={loading}
              data-testid="run-test-button"
              className="w-full flex items-center justify-center gap-2 px-5 py-3.5 rounded-xl bg-white text-black font-medium text-sm transition-all hover:bg-white/90 disabled:opacity-60 disabled:cursor-wait"
            >
              {loading ? (
                <>
                  <CircleNotch size={16} className="animate-spin" />
                  Running...
                </>
              ) : (
                <>
                  <Play size={16} weight="fill" />
                  Run Test
                </>
              )}
            </button>

            {error && (
              <p className="text-red-400/80 text-xs text-center mt-3">{error}</p>
            )}

            <button
              onClick={() => onComplete?.()}
              className="block mx-auto mt-4 text-xs text-white/25 hover:text-white/40 transition-colors"
            >
              Skip test
            </button>
          </motion.div>
        )}

        {/* ── STEP 3: Test result ── */}
        {step === 'result' && testOutput && (
          <motion.div
            key="result"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -24 }}
            transition={{ duration: 0.3 }}
            className="w-full max-w-lg"
          >
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-emerald-500/10 border border-emerald-500/20 mb-4">
                <CheckCircle size={24} weight="fill" className="text-emerald-400" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-1" data-testid="result-title">
                {testOutput.status === 'success' ? 'First result' : 'Test completed'}
              </h2>
              <p className="text-white/40 text-sm">
                {testOutput.status === 'success'
                  ? deployResult?.success_description || 'Your system produced a result.'
                  : 'The test run completed with issues.'}
              </p>
            </div>

            {/* Output */}
            <div className="mb-5 p-4 rounded-xl bg-white/[0.03] border border-white/8 max-h-64 overflow-y-auto" data-testid="test-output">
              <div className="text-xs text-white/40 mb-2">Output</div>
              <div className="text-sm text-white/70 leading-relaxed whitespace-pre-wrap">
                {testOutput.output}
              </div>
            </div>

            <button
              onClick={() => onComplete?.()}
              data-testid="continue-button"
              className="w-full flex items-center justify-center gap-2 px-5 py-3.5 rounded-xl bg-white text-black font-medium text-sm transition-all hover:bg-white/90"
            >
              Continue to Dashboard
              <ArrowRight size={16} />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
